import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Copy, X, ArrowRight } from "@phosphor-icons/react";

/**
 * DuplicateBanner — Yönetici oturum açtığında sistemde yinelenen irsaliye
 * kayıtları varsa üstte küçük bir uyarı şeridi gösterir. "Temizle" tuşuna
 * basıldığında /delivery-notes sayfasına `?duplicates=1` query paramı ile
 * yönlendirilir; DeliveryNotes bu paramı görünce cleanup dialogunu otomatik
 * açar.
 *
 * - Yalnızca yönetici kullanıcı için render edilir.
 * - Aynı oturumda kullanıcı kapatırsa (sessionStorage flag) tekrar gösterilmez.
 * - Poll: sayfa yüklenirken bir kez; ayrıca route değişimlerinde Shell yeniden
 *   mount olmadığı için her 10 dk'da bir sağlama yapar.
 */
export default function DuplicateBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [count, setCount] = useState(0);
  const [groups, setGroups] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  const isAdmin = user && can.delete(user);

  useEffect(() => {
    // Sadece admin — endpoint yönetici gerektirir.
    if (!isAdmin) return;
    // Session içinde kapatıldıysa gösterme.
    if (sessionStorage.getItem("mss-dup-banner-dismissed") === "1") {
      setDismissed(true);
      return;
    }

    let alive = true;
    const check = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/materials/duplicates`);
        if (!alive) return;
        setCount(data.duplicate_extra_count || 0);
        setGroups(data.group_count || 0);
      } catch (_) {
        /* sessizce yok say */
      } finally {
        setLoading(false);
      }
    };
    check();
    // 10 dk'da bir sağlama
    const iv = setInterval(check, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(iv); };
  }, [isAdmin]);

  if (!isAdmin || dismissed || count === 0) return null;

  const openCleanup = () => {
    nav("/irsaliye-arsivi?duplicates=1");
  };

  const dismiss = () => {
    sessionStorage.setItem("mss-dup-banner-dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="duplicate-banner"
      className="bg-amber-50 border-b border-amber-300 px-4 sm:px-6 lg:px-12 py-2.5 flex flex-wrap items-center justify-between gap-3"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-7 h-7 flex-shrink-0 bg-amber-500 flex items-center justify-center">
          <Copy size={14} weight="fill" className="text-white" />
        </div>
        <div className="text-xs sm:text-sm text-amber-900 min-w-0">
          <span className="font-bold" data-testid="duplicate-banner-count">{count}</span>
          <span className="hidden sm:inline"> yinelenen irsaliye kaydı </span>
          <span className="sm:hidden"> yinelenen </span>
          <span className="text-amber-700">({groups} grup)</span>
          <span className="hidden sm:inline"> tespit edildi — veri temizliği önerilir.</span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={openCleanup}
          data-testid="duplicate-banner-cta"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-widest transition-colors"
        >
          Temizle <ArrowRight size={12} weight="bold" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Uyarıyı kapat"
          data-testid="duplicate-banner-dismiss"
          className="p-1.5 text-amber-800 hover:bg-amber-100 rounded-sm transition-colors"
          title="Bu oturumda tekrar gösterme"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
